import React, { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import SEO from '../SEO';
import LandingHeader from '../landing/LandingHeader';
import { getServices, getServicesByCategory } from '../../data/services';
import { generateBreadcrumbJsonLd, seoConfig } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';

const ServicesPage: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  // @ts-ignore
  const { t } = useTranslation('services');
  const i18nInstance = i18n;
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const services = getServices(i18nInstance);
  const categories = ['all', ...Array.from(new Set(services.map(service => service.category)))];
  const currentLang = lang || i18nInstance.language || 'tr';

  const filteredServices = useMemo(() => {
    let filtered = services;

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = getServicesByCategory(selectedCategory, i18nInstance);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(service =>
        service.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        service.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return filtered;
  }, [searchTerm, selectedCategory, services, i18nInstance]);

  const categoryLabels: Record<string, string> = {
    'all': t('categories.all'),
    'social-media': t('categories.socialMedia'),
    'ai-tools': t('categories.aiTools'),
    'analytics': t('categories.analytics'),
    'web-analytics': t('categories.webAnalytics'),
    'advertising': t('categories.advertising')
  };

  const pathWithoutLang = `/hizmetler`;
  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: currentLang === 'en' ? 'Home' : 'Ana Sayfa', url: `https://winnomi.com/${currentLang}/home` },
    { name: currentLang === 'en' ? 'Services' : 'Hizmetler', url: `https://winnomi.com/${currentLang}/hizmetler` },
  ]);

  return (
    <>
      <SEO
        title={currentLang === 'en'
          ? 'Services - Social Media Analytics & AI Tools | Winnomi AI'
          : seoConfig.services.title}
        description={currentLang === 'en'
          ? 'Social media analytics and AI-powered marketing services. Take your sosyal medya marketing performance to the next level.'
          : seoConfig.services.description}
        keywords={currentLang === 'en'
          ? 'social media analytics, AI tools, sosyal medya marketing services, social media management, analytics platform'
          : seoConfig.services.keywords}
        locale={currentLang === 'en' ? 'en_US' : 'tr_TR'}
        alternateLanguages={{
          'tr': `https://winnomi.com/tr${pathWithoutLang}`,
          'en': `https://winnomi.com/en${pathWithoutLang}`,
          'x-default': `https://winnomi.com/tr${pathWithoutLang}`
        }}
        jsonLd={[
          breadcrumbJsonLd,
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: currentLang === 'en' ? 'Winnomi AI Services' : 'Winnomi AI Hizmetleri',
            description: currentLang === 'en'
              ? 'Social media analytics and AI-powered marketing services'
              : 'Sosyal medya analitik ve AI destekli pazarlama hizmetleri',
            provider: {
              '@type': 'Organization',
              name: 'Winnomi AI',
            },
            areaServed: 'TR',
          },
        ]}
      />
      <LandingHeader />
      <div className="min-h-screen bg-gray-900 text-white mt-[-64px] bg-gradient-to-br from-purple-900/20 via-gray-900 to-gray-900">
        {/* Hero Section */}
        <section className="pb-20 px-6 pt-40">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {t('title')}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {t('subtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full transition-all ${selectedCategory === category
                    ? 'bg-purple-600 text-white'
                    : 'bg-white/5 text-gray-300 md:hover:bg-white/10'
                    }`}
                >
                  {categoryLabels[category] || category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {searchTerm ? `${t('searchResults')} (${filteredServices.length})` : t('allServices')}
            </h2>
            {filteredServices.length > 0 && (
              <span className="text-gray-400">
                {filteredServices.length} {t('servicesFound')}
              </span>
            )}
          </div>

          {filteredServices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredServices.map((service) => (
                <Link
                  key={service.id}
                  to={`/${lang}/hizmetler/${service.slug}`}
                  className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden md:hover:border-purple-500/50 transition-all"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={service.seo.ogImage || `/services/${service.id}.jpg`}
                      alt={service.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.parentElement;
                        if (fallback) {
                          fallback.className = `aspect-video ${service.bgColor} flex items-center justify-center`;
                          fallback.innerHTML = `<span class="text-6xl">${service.icon}</span>`;
                        }
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <span className={`inline-block px-3 py-1 ${service.bgColor} ${service.color} rounded-full text-sm mb-3`}>
                      {categoryLabels[service.category] || service.category}
                    </span>
                    <h3 className="text-xl font-semibold mb-2 md:group-hover:text-purple-400 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">
                      {service.shortDescription}
                    </p>

                    {/* Key Features */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-white mb-2">{t('featuresLabel')}</h4>
                      <ul className="space-y-1">
                        {service.features.slice(0, 3).map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-400">
                            <FontAwesomeIcon icon={faCheck} className="text-purple-400 mt-0.5 text-xs" />
                            <span className="line-clamp-1">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <div className="flex items-center text-purple-400 md:group-hover:gap-2 transition-all">
                        <span className="text-sm font-medium">{t('seeDetails')}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs ml-1" />
                      </div>
                      {service.comingSoon && (
                        <span className="text-xs text-yellow-400 bg-yellow-400/20 px-2 py-1 rounded">
                          {t('comingSoon')}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">{t('noServicesFound')}</p>
            </div>
          )}
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">{t('tryServices')}</h2>
            <p className="text-gray-300 mb-6">
              {t('tryServicesDescription')}
            </p>
            <Link
              to={`/${lang}/home`}
              className="inline-block px-6 py-3 bg-purple-600 md:hover:bg-purple-700 rounded-lg transition-colors font-semibold"
            >
              {t('common.getStarted')}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;

